import { PlatformModule } from '@angular/cdk/platform';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, isDevMode, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  InMemoryScrollingOptions,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { createAuthInterceptor, provideRmaTokenProvider } from '@rma/dashboard/authentication/public/api-authentication';
import { provideRmaUserInitialisation } from '@rma/dashboard/authentication/public/user';
import { PaymentMethodAlertServiceModule } from '@rma/dashboard/billing/public/orc-payment-method-alert';
import { provideRmaBillingTokenProvider } from '@rma/dashboard/billing/public/util-proxy';
import { StripeModule } from '@rma/dashboard/generic/orc/stripe';
import { RmaModalModule } from '@rma/dashboard/generic/ui/modal';
import { providePromptRefresh } from '@rma/dashboard/generic/ui/prompt-refresh';
import { PlatformService, provideRmaEnvironment, rmaProperties } from '@rma/dashboard/generic/util/environment';
import { FeatureFlagUserIdentificationService } from '@rma/dashboard/generic/util/feature-flags-user';
import { RmaMaterialModule } from '@rma/dashboard/generic/util/material';
import { provideRmaMetaPixelRouteTracking } from '@rma/dashboard/generic/util/meta-pixel';
import { provideRmaBrandSuffixTitle } from '@rma/dashboard/generic/util/page-meta';
import { traceInterceptor } from '@rma/dashboard/generic/util/traceparent-header-interceptor';
import { localeProviders, provideRmaTranslation } from '@rma/dashboard/generic/util/translation';
import { provideGoogleMaps } from '@rma/generic/maps/api';
import { provideRmaFeatureFlagsBrowser } from '@rma/generic/ng/feature-flags';
import { provideCustomImageLoader } from '@rma/generic/ng/images';
import { provideRmaInstance } from '@rma/generic/ng/instance';
import {
  appendToRmaApiUrl,
  appendToRmaBillingApiUrl,
  appendToRmaMarketorApiUrl,
  appendToRmaReviewsApiUrl,
  appendToRmaSocialApiUrl,
  appendToRmaTrixelsApiUrl,
  appendToRmaWidgetsApiUrl,
  provideRmaApiUrl,
  provideRmaBaseUrl,
  provideRmaBillingApiUrl,
  provideRmaCdnUrl,
  provideRmaFindMyApiUrl,
  provideRmaIdentityUrl,
  provideRmaImagesUrl,
  provideRmaMarketorApiUrl,
  provideRmaReviewsApiUrl,
  provideRmaSocialApiUrl,
  provideRmaStaticImageUrl,
  provideRmaStaticUrl,
  provideRmaTrixelsApiUrl,
  provideRmaWidgetsApiUrl,
  provideSocialStudioUrl,
} from '@rma/generic/ng/util';
import { provideRmaButtonBrand } from '@rma/generic/ui/button-brand';
import { provideRmaPostHog, provideRmaPostHogRouteTracking } from '@rma/generic/util/posthog';
import { Angulartics2RouterlessModule } from 'angulartics2';
import { provideRmaPostHogExceptionContext } from './providers/posthog-exception-context.provider';
import { RmaPreloadingStrategy } from './rma-preload.strategy';
import { appRoutes } from './routes';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const rmaProviders = [RmaMaterialModule.forRoot()];

const moduleProviders = [
  Angulartics2RouterlessModule.forRoot(),

  PlatformModule,
  PaymentMethodAlertServiceModule,
  StripeModule.forRoot(rmaProperties.stripePublishKey),
];

// These are providers we need a the the global level because they are used in services.
// This bloat our bundle size and should be removed when we make our own dialog service
const hackProviders = [RmaModalModule];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    importProvidersFrom(...rmaProviders, ...moduleProviders, ...hackProviders),
    provideRmaTokenProvider(),
    provideHttpClient(
      withXhr(),
      withInterceptorsFromDi(),
      withInterceptors([
        traceInterceptor,
        createAuthInterceptor([
          appendToRmaApiUrl,
          appendToRmaBillingApiUrl,
          appendToRmaMarketorApiUrl,
          appendToRmaReviewsApiUrl,
          appendToRmaSocialApiUrl,
          appendToRmaTrixelsApiUrl,
          appendToRmaWidgetsApiUrl,
        ]),
      ]),
    ),
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling(scrollConfig),
      withPreloading(RmaPreloadingStrategy),
    ),
    provideRmaTranslation(),
    providePromptRefresh(),
    provideAnimations(),
    provideRmaMetaPixelRouteTracking(rmaProperties.facebookPixelId),
    provideRmaEnvironment({ ...rmaProperties }),
    provideGoogleMaps(rmaProperties.googleMapKey, rmaProperties.countryCode),
    provideRmaBrandSuffixTitle(),
    provideRmaButtonBrand('dashboard'),

    provideRmaFeatureFlagsBrowser(rmaProperties.launchDarklyClientId, FeatureFlagUserIdentificationService),

    // Alongside LaunchDarkly, not replacing it. PostHog is the app's only error reporter.
    provideRmaPostHog(rmaProperties.posthogProjectToken, rmaProperties.posthogHost, 'dashboard', PlatformService, {
      registerErrorHandler: true,
      recordSessions: true,
      config: {
        capture_pageview: false,
        capture_pageleave: true,
        person_profiles: 'identified_only',
        // Recording starts only once the user is known, so an impersonated session is never recorded, even briefly.
        disable_session_recording: true,
      },
    }),
    provideRmaPostHogRouteTracking(),
    provideRmaPostHogExceptionContext(),

    provideRmaUserInitialisation(),
    ...localeProviders,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // setting up urls
    provideRmaInstance(rmaProperties.tenant, rmaProperties.environment),
    provideCustomImageLoader(),
    provideRmaApiUrl(),
    provideRmaBaseUrl(),
    provideRmaBillingApiUrl(),
    provideRmaBillingTokenProvider(),
    provideRmaCdnUrl(),
    provideRmaFindMyApiUrl(),
    provideRmaIdentityUrl(),
    provideRmaImagesUrl(),
    provideRmaMarketorApiUrl(),
    provideRmaReviewsApiUrl(),
    provideRmaSocialApiUrl(),
    provideRmaStaticImageUrl(),
    provideRmaStaticUrl(),
    provideRmaTrixelsApiUrl(),
    provideRmaWidgetsApiUrl(),
    provideSocialStudioUrl(),
  ],
};
